import React from 'react';
import { X, Terminal, Copy, CheckCircle } from 'lucide-react';
import { DEBUG_COMMANDS } from '../../constants/debugCommands';
import { useState } from 'react';

interface DevModeReferenceProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DevModeReference({ isOpen, onClose }: DevModeReferenceProps) {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  if (!isOpen) return null;

  const copyToClipboard = (command: string) => {
    navigator.clipboard.writeText(command).then(() => {
      setCopiedCommand(command);
      setTimeout(() => setCopiedCommand(null), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-700">
        {/* Header */}
        <div className="bg-gray-800 px-6 py-4 flex items-center justify-between border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Terminal className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-semibold text-white">Dev Mode Reference</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
          {/* Instructions */}
          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <h3 className="text-cyan-400 font-semibold mb-2">How to Use Dev Mode</h3>
            <ol className="list-decimal list-inside text-gray-300 space-y-1">
              <li>Press <kbd className="bg-gray-700 px-2 py-1 rounded text-sm">F12</kbd> to toggle dev mode</li>
              <li>Open your browser's developer console (<kbd className="bg-gray-700 px-2 py-1 rounded text-sm">Ctrl+Shift+I</kbd> or <kbd className="bg-gray-700 px-2 py-1 rounded text-sm">Cmd+Option+I</kbd>)</li>
              <li>Type <code className="bg-gray-700 px-2 py-1 rounded text-cyan-400">debug.help()</code> to see available commands</li>
              <li>Click any command below to copy it to clipboard</li>
            </ol>
          </div>

          {/* Commands */}
          <div className="space-y-6">
            {Object.entries(DEBUG_COMMANDS).map(([key, section]) => (
              <div key={key} className="bg-gray-800 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-white">{section.title}</h3>
                <div className="space-y-2">
                  {section.commands.map((cmd, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between group hover:bg-gray-700/50 p-2 rounded cursor-pointer transition-colors"
                      onClick={() => copyToClipboard(cmd.command)}
                    >
                      <div className="flex-1">
                        <code className="text-cyan-400 font-mono text-sm">
                          {cmd.command}
                        </code>
                        <span className="text-gray-400 text-sm ml-4">
                          - {cmd.description}
                        </span>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        {copiedCommand === cmd.command ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Examples */}
          <div className="bg-gray-800 rounded-lg p-4 mt-6">
            <h3 className="text-cyan-400 font-semibold mb-3">Quick Examples</h3>
            <div className="space-y-2 font-mono text-sm">
              <div className="text-gray-300">
                <span className="text-gray-500"># Add $1 million</span><br />
                <code className="text-cyan-400">debug.company.addCash(1000000)</code>
              </div>
              <div className="text-gray-300 mt-3">
                <span className="text-gray-500"># Set game to max speed slot</span><br />
                <code className="text-cyan-400">debug.time.setSpeed(4)</code>
              </div>
              <div className="text-gray-300 mt-3">
                <span className="text-gray-500"># Unlock all research instantly</span><br />
                <code className="text-cyan-400">debug.research.unlockAll()</code>
              </div>
              <div className="text-gray-300 mt-3">
                <span className="text-gray-500"># View all competitors</span><br />
                <code className="text-cyan-400">await debug.competitors.list()</code>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-4 mt-6">
            <h3 className="text-yellow-400 font-semibold mb-2">💡 Tips</h3>
            <ul className="list-disc list-inside text-gray-300 space-y-1 text-sm">
              <li>Commands with <code className="bg-gray-700 px-1 rounded">await</code> are asynchronous - include it when copying</li>
              <li>Product IDs can be found using <code className="bg-gray-700 px-1 rounded">debug.products.list()</code></li>
              <li>Company IDs can be found using <code className="bg-gray-700 px-1 rounded">debug.competitors.list()</code></li>
              <li>Game automatically sets to the fastest speed slot when dev mode is enabled</li>
              <li>All changes are immediate - be careful with game balance!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
